import { Box, Flex, HStack, Text, useToken } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { isComplete as isCompleteFn, progressPercent } from "../reviewLogic";

/**
 * ProgressMeter — radial SVG arc with the fraction rendered INSIDE the ring and
 * an optional caption to the right (PLO-4803). The arc draws in from empty on
 * mount (butt caps) and flips azurite → success green once complete. Ports
 * design.tsx `ProgressMeter` (~lines 263–304) using production theme tokens.
 */
export type MeterSize = "xs" | "sm" | "md" | "lg";

export interface ProgressMeterProps {
  /** short caption beside the ring, e.g. "Accounts reviewed" (omit for none) */
  label?: string;
  value: number;
  total: number;
  size?: MeterSize;
  /** force the success (green) tone; otherwise derived from value >= total */
  complete?: boolean;
}

const DIM: Record<MeterSize, number> = { xs: 34, sm: 42, md: 56, lg: 60 };
const STROKE: Record<MeterSize, number> = { xs: 2.5, sm: 3, md: 4, lg: 4 };
const COUNT_FS: Record<MeterSize, string> = {
  xs: "2xs",
  sm: "xs",
  md: "sm",
  lg: "md",
};

const ProgressMeter = ({
  label,
  value,
  total,
  size = "md",
  complete,
}: ProgressMeterProps) => {
  const dim = DIM[size];
  const stroke = STROKE[size];
  const r = (dim - stroke) / 2;
  const c = dim / 2;
  const circ = 2 * Math.PI * r;
  const pct = progressPercent(value, total) / 100;
  const done = complete ?? isCompleteFn(value, total);

  const [track, azurite, success] = useToken("colors", [
    "neutral.100",
    "azurite.600",
    "success.600",
  ]);
  const fg = done ? success : azurite;

  // Draw the arc in from empty on mount (and re-ease on any value change).
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <HStack spacing="md" flex="none" align="center">
      <Box position="relative" w={`${dim}px`} h={`${dim}px`} flexShrink={0}>
        <svg
          width={dim}
          height={dim}
          viewBox={`0 0 ${dim} ${dim}`}
          style={{ transform: "rotate(-90deg)" }}
          aria-hidden
        >
          <circle
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={track}
            strokeWidth={stroke}
          />
          <circle
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={fg}
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - (drawn ? pct : 0))}
            style={{
              transition:
                "stroke-dashoffset 0.9s cubic-bezier(0.22, 1, 0.36, 1), stroke 0.4s ease",
            }}
          />
        </svg>
        <Flex position="absolute" inset="0" align="center" justify="center">
          <Text
            fontFamily="heading"
            fontWeight={500}
            fontSize={COUNT_FS[size]}
            color="text.body.primary"
            lineHeight="1"
          >
            {value}/{total}
          </Text>
        </Flex>
      </Box>
      {label && (
        <Text fontSize="sm" fontWeight={500} color="text.body.tertiary" maxW="96px">
          {label}
        </Text>
      )}
    </HStack>
  );
};

export default ProgressMeter;
